<template>
  <v-dialog v-model="isDialogOpened" :fullscreen="true" :content-class="dialogClass" persistent no-click-animation>
    <component
      :is="currentFormComponent"
      :formData="formData"
      :isFormValid="isFormValid"
      @update:formData="handleUpdateFormData"
      @handleFormValid="handleFormValid"
      @close="closeModal"
      @submit="submitForm" />
  </v-dialog>
</template>

<script>
import LeadFormMobile from '@/components/lead/form/LeadFormMobile.vue';
import LeadFormDesktop from '@/components/lead/form/LeadFormDesktop.vue';
import { createLead } from '@/services/leadService';
import { pushDataLayer } from '@/utils';
import { resetBlogViewCount } from '@/utils/blogViewCounter';
import { saveLeadDataSafely } from '@/services/leadDataService';

export default {
  name: 'LeadModal',
  components: {
    LeadFormMobile,
    LeadFormDesktop,
  },
  props: {
    showModal: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: 'STORE',
    },
    selectedPlan: {
      type: Number,
    },
    selectedPackage: {
      type: Number,
    },

    sourceRoute: {
      type: String,
    },
    contentsId: {
      type: [Number, String],
      default: null,
    },
  },

  data() {
    return {
      formData: {
        type: this.type || 'STORE',
        name: '',
        x: '',
        y: '',
        contact: '',
        roadAddress: '',
        availableTime: '시간 무관',
        email: '',
        planMonths: this.selectedPlan,
        packagePrice: this.selectedPackage,
        isPrivacyAgreed: false,
        isMarketingAgreed: false,
      },
      isFormValid: false,
    };
  },

  computed: {
    // 데스크탑/모바일 동적 컴포넌트
    currentFormComponent() {
      return this.$vuetify.breakpoint.mdAndUp ? 'LeadFormDesktop' : 'LeadFormMobile';
    },

    isDialogOpened: {
      get() {
        return this.showModal;
      },
      set(value) {
        this.$emit('update:showModal', value);
      },
    },

    dialogClass() {
      return this.$vuetify.breakpoint.mdAndUp ? 'lead-modal-desktop' : 'lead-modal-mobile';
    },
  },

  watch: {
    showModal(newVal) {
      if (newVal) {
        // 모달 열릴 때: body 스크롤 막기 (데스크탑만)
        if (this.$vuetify.breakpoint.mdAndUp) {
          document.body.style.overflow = 'hidden';
          document.body.style.position = 'fixed';
          document.body.style.width = '100%';
        } else {
          // 모바일: overflow만 숨기기 (position fixed 제거)
          document.body.style.overflow = 'hidden';
        }

        this.formData = {
          type: this.type || 'STORE',
          name: '',
          x: '',
          y: '',
          contact: '',
          roadAddress: '',
          availableTime: '시간 무관',
          email: '',
          planMonths: this.selectedPlan,
          packagePrice: this.selectedPackage,
          isPrivacyAgreed: false,
          isMarketingAgreed: false,
        };

        pushDataLayer({
          event: 'impressionLeadForm',
          page: this.sourceRoute,
          objectType: 'modal',
          objectSection: 'modal',
          objectId: 'leadForm',
        });
      } else {
        // 모달 닫힐 때: body 스크롤 복구
        document.body.style.overflow = '';
        if (this.$vuetify.breakpoint.mdAndUp) {
          document.body.style.position = '';
          document.body.style.width = '';
        }
      }
    },

    type: {
      handler(newVal) {
        this.formData.type = newVal;
      },
      immediate: true,
    },

    selectedPlan: {
      handler(newVal) {
        this.formData.planMonths = newVal;
      },
      immediate: true,
    },
    selectedPackage: {
      handler(newVal) {
        this.formData.packagePrice = newVal;
      },
      immediate: true,
    },
  },
  methods: {
    handleUpdateFormData(data) {
      this.formData = data;
    },
    handleFormValid(isValid) {
      this.isFormValid = isValid;
    },
    closeModal() {
      this.isDialogOpened = false;
      // 모달 닫을 때 body 스크롤 복구 (중복 방지)
      document.body.style.overflow = '';
      if (this.$vuetify.breakpoint.mdAndUp) {
        document.body.style.position = '';
        document.body.style.width = '';
      }
    },
    // 도입문의 제출
    async submitForm() {
      try {
        pushDataLayer({
          event: 'clickLeadSubmitBtn',
          page: this.sourceRoute,
          objectType: 'button',
          objectSection: 'modal',
          objectId: 'leadSubmit',
          objectName: '무료 상담하기',
        });

        const response = await createLead(this.formData);

        if (response.data) {
          const leadId = response.data.id;
          const companyName = this.formData.name;
          const x = this.formData.x;
          const y = this.formData.y;
          const roadAddress = this.formData.roadAddress;
          const surveyCompleted = response.data.surveyCompleted || false;
          const type = this.formData.type;

          if (leadId) {
            const newLeadData = {
              leadId,
              companyName,
              roadAddress,
              x,
              y,
              type,
            };

            saveLeadDataSafely(newLeadData);

            // GTM 이벤트 전송
            const eventData = {
              event: 'submitLeadForm',
              page: this.sourceRoute,
              objectId: leadId,
              adCategory: this.formData.type,
              companyName: this.formData.name,
            };

            if (this.contentsId) {
              eventData.contentsId = this.contentsId;
            }

            pushDataLayer(eventData);

            resetBlogViewCount(); //  퍼널 시작 - count 0으로 리셋

            const toastData = {
              message: '상담 신청이 완료되었습니다.',
              timestamp: Date.now(),
            };
            localStorage.setItem('leadCompleteToast', JSON.stringify(toastData));

            if (this.formData.type === 'PRODUCT' || surveyCompleted) {
              const signupUri = `/signup?leadId=${leadId}`;
              this.$router.push(signupUri);
              this.closeModal();
              return;
            }

            const surveyUri = `/survey?leadId=${leadId}`;
            this.$router.push(surveyUri);
          }
        }

        this.closeModal();
      } catch (err) {
        console.log(err);
      }
    },
  },
};
</script>

<style scoped lang="scss">
/* 데스크탑 모달 */
::v-deep .lead-modal-desktop {
  border-radius: 20px !important;
}

/* 모바일 모달 */
::v-deep .lead-modal-mobile {
  margin: 0 !important;
  border-radius: 0 !important;
  max-height: 100% !important;
  width: 100% !important;
}
</style>
